<template>
  <div class="min-h-screen bg-cover bg-center bg-no-repeat relative" 
       :style="{ backgroundImage: 'url(/images/login-bg.jpg)' }">
    <!-- Dark overlay for better readability -->
    <div class="absolute inset-0 bg-black bg-opacity-40"></div>
    
    <!-- Header with branding and language switcher -->
    <div class="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-6">
      <div class="flex justify-between items-center">
        <!-- SONNENGLAS branding -->
        <div class="text-white">
          <h1 class="text-lg font-semibold">SONNENGLAS® Partner Portal</h1>
        </div>
        
        <!-- Language switcher -->
        <div class="flex items-center space-x-2">
          <a href="#" 
             @click.prevent="switchLanguage('de')"
             class="text-white hover:text-gray-200 text-sm font-medium px-2 py-1 rounded"
             :class="{ 'bg-white bg-opacity-20': currentLocale === 'de' }">
            🇩🇪 DE
          </a>
          <a href="#" 
             @click.prevent="switchLanguage('en')"
             class="text-white hover:text-gray-200 text-sm font-medium px-2 py-1 rounded"
             :class="{ 'bg-white bg-opacity-20': currentLocale === 'en' }">
            🇺🇸 EN
          </a>
          <a href="#" 
             @click.prevent="switchLanguage('en')"
             class="text-white hover:text-gray-200 text-sm font-medium">
            Login
          </a>
        </div>
      </div>
    </div>

    <!-- Main login content -->
    <div class="relative z-10 min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div class="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div class="mb-6">
          <h2 class="text-center text-2xl font-bold text-gray-900 mb-2">
            {{ $t('Login') }}
          </h2>
          <p class="text-center text-sm text-gray-600">
            {{ $t('Verwende dein Konto, um Partnervorschläge anzuzeigen') }}
          </p>
        </div>
        
        <form class="space-y-6" @submit.prevent="submit">
          <div class="space-y-4">
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t('E-Mail-Adresse') }}
              </label>
              <input
                id="email"
                v-model="form.email"
                name="email"
                type="email"
                autocomplete="email"
                required
                class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                :placeholder="$t('E-Mail-Adresse eingeben')"
              />
              <div v-if="form.errors.email" class="text-red-600 text-sm mt-1">
                {{ form.errors.email }}
              </div>
            </div>
            
            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t('Password') }}
              </label>
              <input
                id="password"
                v-model="form.password"
                name="password"
                type="password"
                autocomplete="current-password"
                required
                class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                :placeholder="$t('Passwort eingeben')"
              />
              <div v-if="form.errors.password" class="text-red-600 text-sm mt-1">
                {{ form.errors.password }}
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <input
                id="remember"
                v-model="form.remember"
                name="remember"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label for="remember" class="ml-2 block text-sm text-gray-900">
                {{ $t('Angemeldet bleiben') }}
              </label>
            </div>
            
            <div class="text-sm">
              <a href="#" 
                 @click.prevent="goToPasswordReset"
                 class="font-medium text-blue-600 hover:text-blue-500">
                {{ $t('Forgot Your Password?') }}
              </a>
            </div>
          </div>

          <div>
            <button
              type="submit"
              :disabled="form.processing"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="form.processing" class="absolute left-0 inset-y-0 flex items-center pl-3">
                <svg class="h-5 w-5 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
              {{ $t('Login') }}
            </button>
          </div>
          
          <!-- Links to other auth pages -->
          <div class="text-center">
            <div class="text-sm text-gray-600">
              {{ $t("Don't have an account?") }} 
              <a href="#" 
                 @click.prevent="goToActivation"
                 class="font-medium text-blue-600 hover:text-blue-500 ml-1">
                {{ $t('Activate account') }}
              </a>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Footer branding -->
    <div class="absolute bottom-6 left-6 z-10">
      <div class="text-white text-sm opacity-80">
        <span class="font-semibold">Sonnenglas</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useForm, router } from '@inertiajs/vue3'
import { ref, onMounted, inject } from 'vue'

// Inject Ziggy route helper for script setup
const route = inject('route') as any

interface Props {
  status?: string
}

defineProps<Props>()

const currentLocale = ref('en')

const form = useForm({
  email: '',
  password: '',
  remember: false,
})

onMounted(() => {
  // Get current locale from browser or localStorage
  currentLocale.value = localStorage.getItem('locale') || 'en'
})

const submit = () => {
  form.post(route('login'), {
    onFinish: () => form.reset('password'),
  })
}

const switchLanguage = (locale: string) => {
  currentLocale.value = locale
  localStorage.setItem('locale', locale)

  // Direct navigation to language switching route for full page reload
  window.location.href = `/language/${locale}`
}

const goToPasswordReset = () => {
  router.visit(route('password.request'))
}

const goToActivation = () => {
  router.visit(route('activate'))
}
</script>